/**
 * THE STAMPING MOMENT (DECISIONS #101).
 *
 * When an activity is saved in a country that is new to the passport, the
 * server stores it with `seen: false`. This global layer picks those up — after
 * a save (`requestPassportCheck`), on app start and when the app returns to
 * the foreground — and plays them one by one: the passport pops up, the cover
 * swings open, and the stamp with the country name and "APPROVED" slams onto
 * the page. Closing marks the country as seen on the server.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, apiConfigured, type PassportCountry } from '@/lib/api';
import { useAuth } from '@/hooks/AuthProvider';
import { useBackHandler } from '@/hooks/useBackHandler';
import { getLocale } from '@/i18n';
import { useT } from '@/i18n/react';
import { countryName } from '@/lib/country';
import { PASSPORT_CHECK_EVENT, formatStampDate } from '@/lib/passportEvents';
import { Button } from '@/components/ui';
import { PassportCover, PassportPage, PassportStamp } from './PassportGraphics';

/** Foreground checks are throttled; an explicit request always goes through. */
const RECHECK_MIN_MS = 60_000;
/** When the stamp lands (cover 0.35 s + 0.75 s, then the slam). */
const STAMP_DELAY_MS = 1000;
const STAMP_LAND_MS = STAMP_DELAY_MS + 330;

export function PassportStampHost() {
  const { user } = useAuth();
  const uid = user?.uid ?? '';
  const t = useT().passport;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [queue, setQueue] = useState<PassportCountry[]>([]);
  const lastCheck = useRef(0);
  const checking = useRef(false);

  const check = useCallback(
    async (force: boolean) => {
      if (!uid || !apiConfigured || checking.current) return;
      if (!force && Date.now() - lastCheck.current < RECHECK_MIN_MS) return;
      checking.current = true;
      lastCheck.current = Date.now();
      try {
        const result = await api.passport();
        queryClient.setQueryData(['passport', uid], result);
        const unseen = result.countries.filter((country) => !country.seen).sort((a, b) => a.unlockedAt - b.unlockedAt);
        if (unseen.length > 0) {
          setQueue((current) => {
            const queued = new Set(current.map((country) => country.iso));
            return [...current, ...unseen.filter((country) => !queued.has(country.iso))];
          });
        }
      } catch {
        // Not critical: the stamp comes on the next check.
      } finally {
        checking.current = false;
      }
    },
    [uid, queryClient],
  );

  useEffect(() => {
    void check(true);
  }, [check]);

  useEffect(() => {
    const onRequest = () => void check(true);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check(false);
    };
    window.addEventListener(PASSPORT_CHECK_EVENT, onRequest);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener(PASSPORT_CHECK_EVENT, onRequest);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [check]);

  const current = queue[0];

  // A short buzz when the stamp hits the page, where the device allows it.
  useEffect(() => {
    if (!current) return;
    const timer = window.setTimeout(() => {
      try {
        navigator.vibrate?.(35);
      } catch {
        // Browser policy.
      }
    }, STAMP_LAND_MS);
    return () => window.clearTimeout(timer);
  }, [current]);

  const dismiss = useCallback(
    (openPassport: boolean) => {
      if (!current) return;
      void api.markPassportSeen([current.iso]).catch(() => undefined);
      queryClient.setQueryData<{ countries: PassportCountry[] }>(['passport', uid], (data) =>
        data
          ? { countries: data.countries.map((country) => (country.iso === current.iso ? { ...country, seen: true } : country)) }
          : data,
      );
      setQueue((items) => items.slice(1));
      if (openPassport) navigate('/profil/utlevel');
    },
    [current, navigate, queryClient, uid],
  );

  useBackHandler(Boolean(current), () => dismiss(false));

  if (!current) return null;
  const name = countryName(current.iso, getLocale());

  return (
    <div className="pstamp-host" role="dialog" aria-modal="true" aria-label={t.stampNewTitle} key={current.iso}>
      <div className="pstamp-host__scrim" aria-hidden="true" />
      <div className="pstamp-host__content">
        <div className="pstamp-host__book">
          <PassportPage>
            <PassportStamp iso={current.iso} name={name} date={formatStampDate(current.unlockedAt)} slam slamDelayMs={STAMP_DELAY_MS} />
          </PassportPage>
          <div className="pstamp-host__cover">
            <PassportCover />
          </div>
        </div>
        <div className="pstamp-host__panel">
          <h2 className="pstamp-host__title">{t.stampNewTitle}</h2>
          <span className="pstamp-host__country">{name}</span>
          <div className="pstamp-host__actions">
            <Button block onClick={() => dismiss(false)}>{t.stampContinue}</Button>
            <Button variant="secondary" block onClick={() => dismiss(true)}>{t.stampOpen}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
